import { useEffect, useRef, useState } from "react"
import Head from "next/head"
import { BlitzPage } from "@blitzjs/next"
import Layout from "app/core/layouts/Layout"
import { PlayIcon, PauseIcon } from "@radix-ui/react-icons"
import dynamic from "next/dynamic"
import { SPANISH_METHODS_AND_DATA_TABS } from "app/features/methods-and-data/methods-and-data.constants"
import { Tab } from "app/features/methods-and-data/components/tab"
import { TranslatedAccordion } from "app/features/methods-and-data/components/translated-accordion"
import { LOCAL_DATA_GLOSSARY_TRANSLATION_KEYS } from "app/features/i18n/i18n.constants"
import Link from "next/link"
import { TesLogo } from "components/tes_logo"
import MainLanguageSwitch from "app/features/i18n/components/main-switch"
import { clearMapPositionSessionStorage } from "hooks/use_map"
import { useVideo } from "hooks/use_video"
import { useTranslation } from "react-i18next"
import { PRVIReleaseBanner } from "app/features/dashboard/components/release-banner"
// Dynamic import to avoid SSR and hydration errors
const MethodsPanel = dynamic(() => import("app/features/methods-and-data/components/methods"), {
  ssr: false,
})
const DataDownload = dynamic(
  () => import("app/features/methods-and-data/components/data-download"),
  { ssr: false }
)
const GuidesAndFAQs = dynamic(
  () => import("app/features/methods-and-data/components/guides-and-faqs.es"),
  { ssr: false }
)

function Benefit({ src, children }: { src: string; children: React.ReactNode }) {
  return (
    <div className="m-auto text-center">
      <img alt="" src={src} className="m-auto max-h-10 max-w-10" />
      <div className="pt-2 text-xs font-bold uppercase leading-tight tracking-wider text-gray-700">
        {children}
      </div>
    </div>
  )
}

const stats = [
  {
    icon: "/icons/neighborhood.svg",
    value: "200K+",
    label:
      "Índices de Equidad Arbórea disponibles para casi 200,000 vecindarios urbanos en los Estados Unidos.",
  },
  {
    icon: "/icons/people.svg",
    value: "260M+",
    label:
      "Más de 260 millones de personas viven en los vecindarios cubiertos por el Índice de Equidad Arbórea.",
  },
  {
    icon: "/icons/plant-trees.svg",
    value: "500M",
    label:
      "Necesitamos más de 500 millones de árboles nuevos para lograr la Equidad Arbórea en todas las áreas con cobertura arbórea insuficiente.",
  },
  {
    icon: "/icons/leaf-dollar.svg",
    value: "4.5B",
    label:
      "Estos árboles proporcionarían beneficios ecosistémicos valorados en casi $4.5 mil millones cada año.",
  },
]

const Header = () => (
  <div>
    <div className="relative z-50 flex items-center justify-between py-1">
      <div className="pl-[2%] lg:pl-[10%]">
        <Link href="/es">
          <TesLogo className="w-20 border-r border-none border-gray-300 pr-[4%] sm:w-24 md:inline md:w-28 md:border-solid" />
          <img className="hidden w-52 md:inline" src="/af-logo.svg" alt="American Forests" />
        </Link>
      </div>
      <div className="flex items-center gap-x-4 py-5 pl-4 pr-[2%] sm:pl-0 lg:gap-x-10 lg:pr-[10%] xl:gap-x-20">
        <Link href="/map?lang=es">
          <button
            onClick={clearMapPositionSessionStorage}
            className="text-right text-xs font-bold leading-tight text-brand-green-dark decoration-[#f9c793] decoration-4 underline-offset-8 hover:underline sm:text-sm md:text-lg"
          >
            Mapa Nacional
          </button>
        </Link>
        <MainLanguageSwitch />
      </div>
    </div>
  </div>
)

const HomeEs: BlitzPage = () => {
  const video = useRef<HTMLVideoElement>(null)
  const { toggleVideoPlay, isPlaying } = useVideo(video)
  const [activeTab, setActiveTab] = useState("methods")
  const { i18n } = useTranslation()
  useEffect(() => {
    if (i18n?.language !== "es") {
      i18n?.changeLanguage("es")
    }
  }, [i18n])

  return (
    <div>
      <Head>
        <meta
          name="description"
          content="Impulsar la equitatividad arbórea en ciudades y localidades de Estados Unidos."
        />
        <meta property="og:image" content="/hero_video_still.jpg" key="image" />
      </Head>
      <div className="bg-white">
        <Header />
      </div>
      <PRVIReleaseBanner lng="es" />
      <section className="relative h-[78rem] md:h-[60rem] lg:h-[66rem]">
        <video
          id="hero-video"
          className="top-100 absolute left-0 z-[-1] hidden aspect-[1280/720] w-full object-cover md:block md:h-3/5 lg:h-5/6"
          loop
          autoPlay
          playsInline
          muted
          ref={video}
          src="https://d17m5nraxo9zm6.cloudfront.net/hero-video.webm"
          poster="https://d17m5nraxo9zm6.cloudfront.net/hero_video_still.jpg"
        ></video>
        <button
          onClick={toggleVideoPlay}
          className="absolute right-4 top-4 z-10 hidden h-6 w-6 items-center justify-center rounded-full border border-white text-white md:flex"
        >
          {!isPlaying && <PlayIcon />}
          {isPlaying && <PauseIcon />}
        </button>
        <img
          src="https://d17m5nraxo9zm6.cloudfront.net/hero_video_still.jpg"
          className="top-100 absolute left-0 z-[-1] block h-5/6 w-full object-cover md:hidden"
        />
        <div className="m-auto w-11/12 pt-[10%] sm:pt-[6%] md:pt-[5%] lg:pt-[7%] xl:w-2/3">
          <h1 className="w-full text-[32px] font-bold leading-[38px] text-white [text-shadow:_0_1px_10px_rgb(0_0_0_/_80%)] md:text-[46px] md:leading-[50px] lg:w-3/5 lg:text-[52px] lg:leading-[58px] xl:w-3/5">
            Ayuda a crear Equidad Arbórea, “Tree Equity”, en comunidades de todo Estados Unidos.
          </h1>
          <div className="pt-6">
            <Link href="/map?lang=es">
              <button
                onClick={clearMapPositionSessionStorage}
                className="inline-block cursor-pointer rounded-full bg-brand-green-dark px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-green sm:text-base"
              >
                Consultar tu puntuación de equitatividad arbórea.
              </button>
            </Link>
          </div>
        </div>
      </section>
      <section className="mx-auto flex max-w-6xl flex-col items-center bg-white pb-24 md:flex-row">
        <div className="flex w-full justify-center md:w-1/2">
          <img
            src="/NE_Laptop.png"
            alt="Explorador nacional: explorar nuestro mapa nacional insignia. Consultar la puntuación de una región en particular y contribuir a la equitatividad arbórea."
            className="w-full min-w-[300px] md:min-w-[600px]"
          />
        </div>
        <div className="mt-8 w-full md:mt-0 md:w-1/2 md:pl-12">
          <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
            Conocer más sobre el{" "}
            <span className="text-brand-green-dark">Índice de Equitatividad Arbórea</span>
          </h2>
          <p className="mb-4 text-base text-gray-700 md:text-lg">
            Los árboles son una infraestructura urbana esencial; además, son cruciales para la salud
            pública y el bienestar colectivo. El Índice de Equitatividad Arbórea se diseñó como una
            herramienta para abordar la perjudicial desigualdad ambiental al priorizar las
            inversiones dirigidas a los habitantes de zonas con mayor necesidad.
          </p>
        </div>
      </section>
      <section className="bg-[#0B4C4C] py-32 text-white">
        <div className="mx-auto flex max-w-6xl flex-col items-center md:flex-row">
          <div className="mb-8 w-full md:mb-0 md:w-1/2 md:pr-12">
            <h3 className="mb-4 text-2xl font-bold md:text-3xl">
              El cambio impulsado por la ciencia
            </h3>
            <Link href="/map?lang=es">
              <button
                className="mb-6 rounded border border-white px-6 py-2 text-white transition hover:bg-white hover:text-[#0B4C4C]"
                onClick={clearMapPositionSessionStorage}
              >
                VER EL MAPA
              </button>
            </Link>
          </div>
          <div className="w-full text-sm md:w-1/2">
            <p>
              El Índice de Equitatividad Arbórea mide la efectividad de los doseles forestales
              urbanos en las zonas con mayor necesidad. A partir de estos datos, se establecen
              criterios que colocan la equitatividad al centro de las inversiones que se destinan a
              comunidades de color, personas de bajos ingresos y poblaciones afectadas, de manera
              desproporcionada, por el calor intenso, la contaminación y otros peligros ambientales.
            </p>
          </div>
        </div>
      </section>
      <section
        className="bg-[#F7FAF9] bg-cover bg-center py-12"
        style={{
          backgroundImage: "url(/tes-footer-gradient.jpg)",
        }}
      >
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-2 font-bold text-gray-800">
            El Índice de Equitatividad Arbórea recopila información proveniente de múltiples fuentes
            para precisar una puntuación única, que oscila entre 0 y 100. Cuanto más baja es la
            puntuación, mayor es la necesidad de inversión.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-4">
            <Benefit src="/icons/tree.svg">Cobertura de dosel arbóreo </Benefit>
            <Benefit src="/icons/buildingdensity.svg">Densidad de edificios</Benefit>
            <Benefit src="/icons/income.svg">Nivel de ingresos y empleo</Benefit>
            <Benefit src="/icons/race.svg">Raza</Benefit>
            <Benefit src="/icons/temp.svg">Temperatura de la superficie</Benefit>
            <Benefit src="/icons/health.svg">Salud</Benefit>
            <Benefit src="/icons/language.svg">Idioma</Benefit>
            <Benefit src="/icons/age.svg">Edad</Benefit>
          </div>
        </div>
      </section>
      <section className="bg-white py-20">
        <div className="mx-auto max-w-3xl rounded border-2 border-brand-green-dark p-8 text-center">
          <h4 className="mb-2 text-xl font-bold uppercase text-brand-green-dark md:text-2xl">
            Nuestro objetivo es que las comunidades urbanas tengan los recursos para obtener árboles
            saludables para quienes más los necesitan.
          </h4>
        </div>
      </section>
      <section className="bg-gradient-to-b from-white to-[#ECF5F2] pb-32 pt-16">
        <div className="mx-auto max-w-6xl">
          <h5 className="mb-8 text-center text-body font-bold text-brand-green-dark">
            ÍNDICE DE EQUITATIVIDAD ARBÓREA
            <br />
            <span className="text-lg text-black">EN NÚMEROS</span>
          </h5>
          <div className="grid grid-cols-1 gap-16 md:grid-cols-4">
            {stats.map((stat, i) => (
              <div key={i} className="flex flex-col items-start gap-4 text-left">
                <div className="flex flex-row items-center gap-1">
                  <img
                    src={stat.icon}
                    alt=""
                    className="h-8 w-8 min-w-[3rem] text-brand-green-dark"
                  />
                  <span className="text-4xl font-thin text-brand-green-dark">{stat.value}</span>
                </div>
                <p className="text-sm text-gray-700">{stat.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-24 flex flex-col items-stretch justify-center gap-20 md:flex-row">
            <div
              className="flex min-h-[220px] w-full items-center justify-center rounded-xl bg-[#0B4C4C] bg-cover bg-center p-8 md:w-1/2"
              style={{
                backgroundImage: "url(/green-streets-bg.png)",
              }}
            >
              <p className="text-center text-lg text-white">
                Plantar árboles en vecindarios desatendidos aborda derechos humanos básicos de
                salud, seguridad y bienestar. Los beneficios para mitigar el calor extremo, la
                contaminación, la salud pública y los riesgos ambientales están bien documentados.
              </p>
            </div>
            <div className="flex min-h-[220px] w-full flex-col justify-center p-8 md:w-1/2">
              <h3 className="mb-4 text-2xl font-bold text-gray-800">Únete al movimiento</h3>
              <p className="text-base text-gray-700">
                American Forests desarrolló el Índice de Equidad Arbórea para abordar las
                inequidades ambientales en la distribución de árboles comunes en ciudades y pueblos
                de todo EE. UU. El índice establece un estándar de equidad para guiar la inversión
                en infraestructura arbórea urbana crítica, comenzando por los vecindarios con mayor
                necesidad.
                <br />
                <br />
                Creamos el Índice de Equidad Arbórea para que cada persona tenga la información
                necesaria para abogar por la salud y la resiliencia de su comunidad.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="bg-[#ECF5F2]">
          <div className="m-auto flex h-full w-11/12 flex-col justify-between md:w-4/5">
            <div className="m-auto flex w-full flex-row justify-between lg:w-4/5 xl:w-3/5">
              {SPANISH_METHODS_AND_DATA_TABS.map((tab) => (
                <Tab
                  key={tab.slug}
                  label={tab.label}
                  handleClick={() => setActiveTab(tab.slug)}
                  isSelected={activeTab === tab.slug}
                />
              ))}
            </div>
          </div>

          <div className="m-auto flex h-full w-full flex-col justify-between bg-white">
            <div className="m-auto my-16 w-11/12 border-t-2 border-t-brand-green md:w-4/5 lg:w-2/3 xl:w-1/2">
              {activeTab === "methods" && <MethodsPanel />}
              {activeTab === "data-glossary" && (
                <TranslatedAccordion translationKeys={LOCAL_DATA_GLOSSARY_TRANSLATION_KEYS} />
              )}
              {activeTab === "faqs" && <GuidesAndFAQs />}
              {activeTab === "data-download" && <DataDownload />}
            </div>
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-body capitalize">COLABORADORES</p>
              <div className="mt-4 flex flex-row flex-wrap items-center justify-center gap-4">
                <img src="/logos/google.jpg" alt="Google logo" className="h-32 w-32" />
                <img src="/logos/itree.jpg" alt="iTree logo" className="h-32 w-32" />
                <img src="/logos/uvm.jpg" alt="University of Vermont logo" className="h-32 w-32" />
                <img src="/logos/usfs.jpg" alt="US Forest Service logo" className="h-32 w-32" />
                <img src="/logos/icp.jpg" alt="iced coffee please logo" className="h-32 w-32" />
              </div>
            </div>
          </div>
        </div>
      </section>
      <div
        className="flex h-[700px] flex-col items-center justify-center bg-[#ECF5F2] bg-cover bg-center"
        style={{
          backgroundImage: "url(/tes-footer-gradient.jpg)",
        }}
      >
        <img src="/logos/af-logo.svg" alt="American Forests logo" className="w-[350px]" />
        <p className="text-body">
          Hecho posible con datos de cobertura arbórea aportados por
          <img src="/logos/google.svg" alt="google logo" className="mx-2 inline-block w-32" />
          y con el apoyo a la traducción al español por
          <img
            src="/logos/ddf.png"
            alt="Doris Duke Foundation logo"
            className="mx-2 inline-block w-32"
          />
        </p>
        <div className="text-body leading-relaxed text-gray-600">
          Copyright © {new Date().getFullYear()} American Forests. Todos los derechos reservados.
          <Link href="/privacy" className="text-brand-green-dark">
            Política de Privacidad.
          </Link>
        </div>
      </div>
    </div>
  )
}

HomeEs.suppressFirstRenderFlicker = true
HomeEs.getLayout = (page) => <Layout>{page}</Layout>
export default HomeEs
